import { Fragment, useState } from "react";
import { useAuth } from "../../../auth/AuthenticationWrapper";
import AssetsService from "../../../rest/assetsService";
import ProductService from "../../../rest/productService";
import { toast } from "react-toastify";
import InfoField from "../../../components/lib/infoField";
import CustomButton from "../../../components/lib/customButton";
import ConfirmDeleteModal from "../../../components/reusables/confirmDeleteModal";
import { formatCurrency } from "../../../utils/CurrencyUtils";
import { formatOnlyDate } from "../../../utils/DateUtils";
import NutritionalFactsTableView from "../../../components/reusables/nutritionalFactsTableView";
import ProductEditStepper from "./productEditStepper";
import NoPictureWarning from "../../../resources/nopic.png";
import Modal from "../../../components/lib/modal";
import ConfirmCloseView from "../../customers/components/confirmCloseView";
import { useProductLiveUpdate } from "../hooks/useProductLiveUpdate";
import { Transition } from "@headlessui/react";
import SmartImage from "../../../components/reusables/smartImage";

const ProductDetailsModalContent = ({ product, onEdit, onDelete }) => {
    const liveProduct = useProductLiveUpdate(product);
    const { hasPermission } = useAuth();

    const isParent = sessionStorage.getItem('role') === 'PARENT';

    const canEdit = hasPermission("storage", "update") && !isParent;
    const canDelete = hasPermission("storage", "delete") && !isParent;

    const [imageData, setImageData] = useState(null);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (hasData) {
            setConfirmClose(true);
        } else {
            setShowEditModal(false);
        }
    };

    const forceClose = () => {
        setConfirmClose(false);
        setShowEditModal(false);
    };

    const imageUrl = `/foodz/api/v1/uploads/carregarImagemDoProduto?path=${encodeURIComponent(liveProduct?.image_url)}`;

    const handleDelete = async () => {
        try {
            await ProductService.deleteProduct(liveProduct.product_id);
            toast.success("Produto removido com sucesso!");
            setShowModalDelete(false);
            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao remover produto.");
        }
    };

    const handleEditSubmit = async (updatedData) => {
        try {
            await ProductService.updateProduct(liveProduct.product_id, updatedData);
            toast.success('Produto atualizado com sucesso!');
            setShowEditModal(false);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar produto');
        }
    };

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
        >
            <div className="flex flex-col h-[75vh] md:h-[650px] max-h-[80vh]">
                <div className="flex-1 overflow-y-auto px-4 py-2 sm:px-2">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-5/12 space-y-4">
                            <div className="w-full aspect-square sm:aspect-video lg:aspect-square bg-gray-200 rounded-2xl overflow-hidden border border-gray-200 shadow-md relative group">
                                {liveProduct?.image_url ? (
                                    <SmartImage
                                        src={imageUrl}
                                        alt={liveProduct.name}
                                        isThumb={false}
                                        className="object-cover group-hover:scale-105"
                                    />
                                ) : (
                                    <img src={NoPictureWarning} className="absolute inset-0 w-full h-full opacity-40 object-contain p-12" alt="N/A" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100" />

                                {!isParent && (
                                    <div className={`absolute top-3 left-3 z-20 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1 ${liveProduct.active ? "bg-green-500" : "bg-red-500"}`}>
                                        {liveProduct.active ? "ATIVO" : "INATIVO"}
                                    </div>
                                )}

                                <div className="absolute bottom-0 left-0 w-full p-4 z-10">
                                    <h2 className="text-white font-bold text-xl drop-shadow-lg truncate">
                                        {liveProduct.name}
                                    </h2>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className={`grid ${isParent ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                                    {!isParent && <InfoField label="Código de Barras" value={liveProduct.barcode || 'Não Registrado'} />}
                                    <InfoField label="Marca" value={liveProduct.brand || 'Não Registrado'} />
                                </div>

                                <div className={`grid ${isParent ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                                    {!isParent ?
                                        <>
                                            <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                                <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço</p>
                                                <p className="text-lg font-bold text-slate-800">{formatCurrency(liveProduct.price)}</p>
                                            </div>
                                            <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                                <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço Online</p>
                                                <p className="text-lg font-bold text-slate-800">{formatCurrency(liveProduct.online_price)}</p>
                                            </div>
                                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                                <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Estoque</p>
                                                <p className="text-lg font-bold text-slate-800">{liveProduct.quantity}</p>
                                            </div>
                                            
                                        </>
                                        : <>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                                <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço</p>
                                                <p className="text-lg font-bold text-slate-800">{formatCurrency(liveProduct.online_price)}</p>
                                            </div>
                                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                                <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Estoque</p>
                                                <p className="text-lg font-bold text-slate-800">{liveProduct.quantity}</p>
                                            </div>
                                        </>}
                                </div>
                            </div>
                        </div>

                        <div className="w-full lg:w-7/12 flex flex-col gap-4">
                            {!isParent && (
                                <div className="grid grid-cols-2 gap-3">
                                    <InfoField label="SKU" value={liveProduct.sku || 'N/A'} />
                                    <InfoField label="Aviso de Limite" value={liveProduct.limit_warning} />
                                </div>
                            )}

                            <InfoField label="Descrição" value={liveProduct.description} />

                            {!isParent && <InfoField label="Observações" value={liveProduct.observations} />}

                            {!isParent && (
                                <>
                                    <div className="grid grid-cols-2 gap-3">
                                        <InfoField label="Ativo no cardápio?" value={liveProduct.active ? "Sim" : "Não"} />
                                        <InfoField label="Precisa de Preparo?" value={liveProduct.needsPreparation ? "Sim" : "Não"} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <InfoField
                                            label="Fabricação"
                                            value={liveProduct.fabrication_date === 'Invalid Date' ? 'N/A' : formatOnlyDate(liveProduct.fabrication_date)}
                                        />
                                        <InfoField
                                            label="Validade"
                                            value={liveProduct.expiration_date === 'Invalid Date' ? 'N/A' : formatOnlyDate(liveProduct.expiration_date)}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="py-2 overflow-x-auto">
                                <p className="text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest">Tabela Nutricional</p>
                                <NutritionalFactsTableView nutritionalFactsData={liveProduct.nutritional_facts} />
                            </div>
                        </div>
                    </div>
                </div>

                {!isParent && (
                    <div className="mt-auto border-t border-gray-200 bg-gray-50 p-3 sm:p-4 rounded-b-lg flex justify-end gap-3">
                        <CustomButton
                            buttonType="edit"
                            text="Editar Produto"
                            onClick={() => setShowEditModal(true)}
                            disabled={!canEdit}
                            className="px-6"
                        />
                        <CustomButton
                            buttonType="delete"
                            text="Excluir"
                            onClick={() => setShowModalDelete(true)}
                            disabled={!canDelete}
                        />
                    </div>
                )}

                {showEditModal && !isParent && (
                    <Modal isOpen={showEditModal} setIsOpen={requestClose} title="Editar Produto" hideFooter maxWidth="max-w-xl lg:max-w-2xl md:-mr-16">
                        <div className="relative h-full flex flex-col">
                            <ProductEditStepper
                                product={liveProduct}
                                initialImagePreview={imageData}
                                onFinish={async ({ product: productFormValues, nutritional_facts, image }) => {
                                    try {
                                        let image_url = liveProduct.image_url;
                                        if (image) {
                                            const formData = new FormData();
                                            formData.append("file", image);
                                            const upload = await AssetsService.uploadProductImage(formData);
                                            image_url = upload.data;
                                        }
                                        const finalPayload = {
                                            ...productFormValues,
                                            nutritional_facts,
                                            image_url,
                                            clientCode: sessionStorage.getItem("clientCode")
                                        };
                                        await handleEditSubmit(finalPayload);
                                    } catch (uploadError) {
                                        console.error("Erro imagem:", uploadError);
                                        toast.error("Erro ao processar imagem.");
                                    }
                                }}
                                onCancel={requestClose}
                                onDirty={() => setHasData(true)}
                            />
                            {confirmClose && (
                                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                                    <ConfirmCloseView onCancel={() => setConfirmClose(false)} onConfirm={forceClose} />
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                <ConfirmDeleteModal
                    isOpen={showModalDelete}
                    title="Excluir produto"
                    message={`Tem certeza que deseja excluir ${liveProduct.name}?`}
                    warning="Essa ação não poderá ser desfeita."
                    onConfirm={handleDelete}
                    onCancel={() => setShowModalDelete(false)}
                />
            </div>
        </Transition>
    );
};

export default ProductDetailsModalContent;